/**
 * Preseed Column Definitions
 *
 * Column definitions with JSX cell renderers for preseed.
 * Separated from columns.ts to isolate JSX dependencies.
 *
 * Pattern matches:
 * - src/sections/seeds/config/columnDefinitions.tsx
 * - src/sections/asset/config/columnDefinitions.tsx
 */

import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Certificate } from '@peculiar/asn1-x509';

import emptyLogo from '/icons/emptyLogo.svg';
import { OverflowText } from '@/components/OverflowText';
import { getColumnWidth } from '@/components/table/columns';
import { ColumnPriority, Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { SeedStatusComponent } from '@/sections/seeds/components/badges';
import { Preseed, Seed } from '@/types';
import { decodeCertificate } from '@/utils/decodeCertificate.util';
import { Regex } from '@/utils/regex.util';
import { createSortableConfig } from '@/utils/tableConfig.util';

import { formatConfidence } from '@/sections/seeds/config/columnDefinitions';

import { PRESEED_COLUMN_WIDTHS } from './columns';
import { PreseedTypeLabel } from './constants';

// ============================================================================
// CELL RENDERER COMPONENTS
// ============================================================================

/**
 * Render the pattern cell based on preseed type
 */
// eslint-disable-next-line react-refresh/only-export-components
const PatternCell = ({ item }: { item: Preseed }) => {
  switch (item.type) {
    case 'csp': {
      const itemValue =
        item.display === 'base64' ? atob(item.value) : item.value;
      return <div>{itemValue.split(',').length} directives found</div>;
    }
    case 'cik':
      return (
        <div className="flex items-center gap-1">
          <span>{item.title}</span>
          <Tooltip placement="right" title={'Company Name'}>
            <ExclamationCircleIcon
              className="text-chariot-stroke size-4"
              aria-hidden="true"
            />
          </Tooltip>
        </div>
      );
    case 'favicon':
      return (
        <div className="bg-layer0 size-fit rounded-xs p-1.5">
          <img
            className="size-6 text-xs"
            src={item.value}
            alt="favicon"
            loading="lazy"
            onError={e => (e.currentTarget.src = emptyLogo)}
          />
        </div>
      );
    case 'tlscert': {
      const cert = decodeCertificate(item.value as unknown as Certificate);

      if (!cert || cert.subjectCommonNames.length === 0) {
        return <div>-</div>;
      }
      return (
        <div className="flex items-center gap-1">
          <span>{cert.subjectCommonNames.join(', ')}</span>
          <Tooltip placement="right" title={'Subject Common Name'}>
            <ExclamationCircleIcon
              className="text-chariot-stroke size-4"
              aria-hidden="true"
            />
          </Tooltip>
        </div>
      );
    }
    case 'whois+company':
      return 'Company name';
    case 'edgar+company':
      return 'EDGAR entity';
    case 'whois+email':
      return 'E-Mail';
    case 'whois+name':
      return 'Name';
    default:
      return <div>{item.title}</div>;
  }
};

/**
 * Render the value display based on preseed type
 */
// eslint-disable-next-line react-refresh/only-export-components
const ValuesCell = ({ item }: { item: Preseed }) => {
  switch (item.type) {
    case 'csp': {
      const itemValue =
        item.display === 'base64' ? atob(item.value) : item.value;
      const urls = itemValue
        .split(' ')
        .filter(value => Regex.DOMAIN.test(value));

      const uniqueUrls = [...new Set(urls)];

      return <div className="underline">{uniqueUrls.length} URLs found</div>;
    }
    case 'tlscert': {
      const cert = decodeCertificate(item.value as unknown as Certificate);

      return (
        <div className="underline">
          {cert?.dnsNames.length ?? '0'} DNS names found
        </div>
      );
    }

    default:
      return <OverflowText text={item.value} />;
  }
};

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

/**
 * Get preseed columns configuration
 */
export const getPreseedColumns = (): Columns<Preseed> => {
  return [
    {
      id: 'status',
      label: 'Status',
      cell: item => <SeedStatusComponent seed={item as unknown as Seed} />,
      ...PRESEED_COLUMN_WIDTHS.status,
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      id: 'type',
      label: 'Type',
      cell: item => (
        <div className="flex w-full flex-col gap-1 overflow-hidden py-3">
          <OverflowText
            className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary shrink cursor-pointer truncate font-bold"
            text={PreseedTypeLabel[item.type] ?? item.type}
          />

          <ValuesCell item={item} />
        </div>
      ),
      ...PRESEED_COLUMN_WIDTHS.type,
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      id: '',
      label: 'Pattern',
      cell: item => <PatternCell item={item} />,
      sorting: false,
      ...PRESEED_COLUMN_WIDTHS.pattern,
      isExpandable: {
        minWidth: PRESEED_COLUMN_WIDTHS.pattern.minWidth,
      },
    },
    {
      id: 'count',
      label: 'Matches',
      sorting: false,
      ...PRESEED_COLUMN_WIDTHS.count,
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      id: 'confidence',
      label: 'Confidence',
      sorting: false,
      cell: (item: Preseed) => formatConfidence(item.confidence),
      ...getColumnWidth('seed', 'confidence'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
  ];
};

/**
 * Get sortable column configuration for preseed
 *
 * @returns Sortable column configuration with default and disabled columns
 */
export const getPreseedColumnsConfig = () => {
  const columns = getPreseedColumns();

  return createSortableConfig(columns, {
    disabledColumns: ['type'],
    defaultColumns: ['type'],
  });
};
